import React, { useState, useEffect, useCallback } from "react";
import { getSyncHistory, triggerPortfolioRefresh, getRefreshStatus } from "../lib/api";
import { History, CheckCircle2, XCircle, Loader2, AlertTriangle, RefreshCw, Activity } from "lucide-react";

const SOURCE_LABEL = {
  zerodha: "Zerodha",
  icici: "ICICI Direct",
  angel: "Angel One",
  usd_inr: "USD/INR rate",
  india_prices: "India equity prices",
  us_prices: "US equity prices",
  mf_prices: "Mutual Fund NAVs",
  manual_prices: "Manual holdings",
  sectors: "Sector enrichment",
};

function fmtSource(name) {
  if (!name) return "(unknown)";
  const [base, sub] = name.split(":");
  const label = SOURCE_LABEL[base] || base;
  return sub ? `${label} · ${sub}` : label;
}

export default function SyncPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const [logsRes, statusRes] = await Promise.allSettled([
        getSyncHistory(),
        getRefreshStatus(),
      ]);
      if (logsRes.status === "fulfilled") setLogs(logsRes.value.data.logs || []);
      if (statusRes.status === "fulfilled") setRefreshStatus(statusRes.value.data);
    } catch (e) {
      console.warn("[sync-log] load failed", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRunDiagnostics = async () => {
    setRefreshing(true);
    try {
      await triggerPortfolioRefresh();
      // Poll until refresh completes
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const { data } = await getRefreshStatus();
        if (!data.in_progress) {
          setRefreshStatus(data);
          await load();
          break;
        }
      }
    } catch (e) {
      console.error("[sync-log] diagnostics failed", e);
    }
    setRefreshing(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[var(--brand)]" /></div>;

  const sources = refreshStatus?.sources || {};
  const sourceList = Object.entries(sources);

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6" data-testid="sync-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Sync Log</h1>
          <p className="text-sm text-[var(--text-muted)]">Per-source health for every refresh attempt</p>
        </div>
        <button
          onClick={handleRunDiagnostics}
          disabled={refreshing}
          className="bg-cyan-500/10 hover:bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:text-cyan-200 rounded-md px-3 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          data-testid="run-diagnostics-btn"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
          {refreshing ? "Running diagnostics..." : "Run Diagnostics"}
        </button>
      </div>

      {/* Last refresh — per-source status grid */}
      {sourceList.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="refresh-source-grid">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-3 flex items-center gap-2">
            <RefreshCw size={11} /> Last refresh
            {refreshStatus?.last_refresh > 0 && (
              <span className="text-[var(--text-muted)]/70">· {new Date(refreshStatus.last_refresh * 1000).toLocaleString()}</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sourceList.map(([name, s]) => {
              const dotColor = s.status === "success" ? "bg-emerald-400"
                : s.status === "partial" ? "bg-amber-400"
                : s.status === "failed" ? "bg-red-400"
                : s.status === "skipped" ? "bg-white/20"
                : "bg-cyan-400 animate-pulse";
              return (
                <div key={name} className="bg-[var(--bg-base)] border border-white/5 rounded p-2.5" data-testid={`diag-source-${name}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
                      <span className="text-xs text-white truncate">{fmtSource(name)}</span>
                    </div>
                    <span className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">{s.status}</span>
                  </div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)] flex items-center gap-2">
                    <span>{s.count || 0} records</span>
                    <span>· {s.duration_ms || 0}ms</span>
                  </div>
                  {s.error && (
                    <div className="text-[10px] text-red-400/80 mt-1 truncate" title={s.error}>↳ {s.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full history */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md" data-testid="sync-log">
        <div className="px-5 py-3 border-b border-white/5 text-[11px] uppercase tracking-wider text-[var(--text-muted)]">
          History (last {logs.length})
        </div>
        {logs.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-muted)] text-sm">No sync activity yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {logs.map((log, i) => {
              const status = log.status || "";
              const isOk = ["success", "synced", "connected", "imported"].includes(status);
              const isPartial = status === "partial";
              const isFail = ["failed", "error"].includes(status);
              const Icon = isOk ? CheckCircle2 : isPartial ? AlertTriangle : isFail ? XCircle : History;
              const iconColor = isOk ? "text-[var(--positive)]" : isPartial ? "text-amber-400" : isFail ? "text-[var(--negative)]" : "text-[var(--text-muted)]";
              const label = log.source ? fmtSource(log.source) : (log.account_id || log.event || "(unknown)");
              const sub = log.source
                ? `${log.endpoint || ""} · ${log.count || 0} records${log.duration_ms ? ` · ${log.duration_ms}ms` : ""}`
                : `${log.type || ""} · ${log.status || ""} · ${log.records || 0} records`;
              return (
                <div key={log.id || i} className="flex items-center justify-between px-5 py-3 hover:bg-white/[0.02]" data-testid={`sync-log-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon size={16} className={`${iconColor} flex-shrink-0`} />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{label}</div>
                      <div className="text-[10px] font-mono text-[var(--text-muted)] truncate">{sub}</div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-mono text-[var(--text-muted)]">{new Date(log.timestamp).toLocaleString()}</div>
                    {log.error && <div className="text-[10px] text-[var(--negative)] max-w-[300px] truncate" title={log.error}>↳ {log.error}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
